import React, { useCallback, useEffect, useState } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

/**
 * Compact install control for Chromium (beforeinstallprompt). Hidden when unavailable or already standalone.
 */
export const InstallAppButton: React.FC = () => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

  const standalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const onInstall = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }, [deferred]);

  if (standalone || !deferred) return null;

  return (
    <button
      type="button"
      onClick={onInstall}
      className="rounded-lg border border-border bg-surface-muted px-2.5 py-1.5 text-xs font-medium text-fg-secondary hover:bg-surface hover:text-fg"
      title="Install LaunchPoint as an app"
    >
      Install
    </button>
  );
};
